import { caCustomerIdForMetafield, caSetCookieOptInMetafield } from '@/lib/queries.js';
import { getAccessToken as getB2bCustomerAccountAccessToken } from './customerAuth';

const CA = {
  lsAccess: 'customer_account_access_token',
  lsRefresh: 'customer_account_refresh_token',
  lsExpiry: 'customer_account_token_expiry_ms',
  ssPkceVerifier: 'ca_pkce_verifier',
  ssPkceState: 'ca_pkce_state',
  ssOauthReturn: 'ca_oauth_return_url',
  ssPendingCookieOptIn: 'ca_pending_cookie_opt_in',
  ssOauthRedirectUri: 'ca_oauth_redirect_uri',
  lsBridgeResume: 'ca_bridge_resume_customer_account_oauth',
  lsBridgePending: 'ca_bridge_pending_cookie_opt_in'
};

export const EVENT_CUSTOMER_ACCOUNT_PENDING_COOKIE_OPT_IN = 'abg:customer-account:pending-cookie-opt-in';
export const CA_ERROR_MISSING_ACCESS_TOKEN = 'CA_MISSING_ACCESS_TOKEN';

const CUSTOMER_ACCOUNT_OAUTH_SCOPE = 'openid email customer-account-api:full';
const CUSTOMER_ACCOUNT_API_VER_FALLBACK = '2026-04';

const WELL_KNOWN_HEADERS = { Accept: 'application/json' };

const DEFAULT_COOKIE_OAUTH_CALLBACK = '/pages/customer-account-auth-callback';

let cachedGqlUrl = null;
let cachedCustomerApiVersion = null;
let oidcCached = null;
let oidcPromise = null;
let customerAccountRefreshPromise = null;

function getCookieOAuthCallbackPath() {
  if (typeof window === 'undefined') return DEFAULT_COOKIE_OAUTH_CALLBACK;
  const acc = window.customerAccount || (window.siteGlobals && window.siteGlobals.customerAccount);
  const p = acc && acc.cookieOAuthCallbackPath != null ? String(acc.cookieOAuthCallbackPath).trim() : '';
  if (!p) return DEFAULT_COOKIE_OAUTH_CALLBACK;
  return p.startsWith('/') ? p : `/${p}`;
}

function caError(message, code) {
  return Object.assign(new Error(message), { code });
}

function joinApiMessages(entries, maxLen) {
  const s = (entries || [])
    .map(x => (x && x.message) || '')
    .filter(Boolean)
    .join('; ');
  return maxLen ? s.slice(0, maxLen) : s;
}

async function fetchShopWellKnownJson(path) {
  try {
    const r = await fetch(`${window.location.origin}${path}`, { headers: WELL_KNOWN_HEADERS });
    if (!r.ok) return null;
    return await r.json();
  } catch (e) {
    void e;
    return null;
  }
}

function parseCustomerApiVersionFromGraphqlUrl(href) {
  const m = String(href).match(/\/customer\/api\/(\d{4}-\d{2})\/graphql/);
  return m ? m[1] : null;
}

export async function resolveOidcConfiguration() {
  if (typeof window === 'undefined') return null;
  if (oidcCached) return oidcCached;
  if (oidcPromise) return oidcPromise;
  oidcPromise = (async () => {
    const j = await fetchShopWellKnownJson('/.well-known/openid-configuration');
    if (j && typeof j.authorization_endpoint === 'string' && typeof j.token_endpoint === 'string') {
      oidcCached = j;
      return j;
    }
    return null;
  })();
  const out = await oidcPromise;
  oidcPromise = null;
  return out;
}

function base64urlFromBytes(bytes) {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let str = '';
  for (let i = 0; i < u.length; i++) str += String.fromCharCode(u[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256Base64Url(s) {
  const h = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return base64urlFromBytes(new Uint8Array(h));
}

function randomPkceVerifier() {
  const a = new Uint8Array(32);
  crypto.getRandomValues(a);
  return base64urlFromBytes(a);
}

export function getCustomerAccountClientId() {
  if (typeof window === 'undefined') return '';
  const acc = window.customerAccount || (window.siteGlobals && window.siteGlobals.customerAccount);
  const nested = acc && acc.clientId != null ? String(acc.clientId).trim() : '';
  if (!nested || nested.startsWith('no_')) return '';
  return nested;
}

export function getCustomerAccountApiEndpoint() {
  if (typeof window === 'undefined') return '';
  const acc = window.customerAccount || (window.siteGlobals && window.siteGlobals.customerAccount);
  const fromSettings = acc && acc.apiEndpoint != null ? String(acc.apiEndpoint).trim() : '';
  if (!fromSettings || fromSettings.startsWith('no_') || !/\/customer\/api\/[^/]+\/graphql$/.test(fromSettings)) {
    return '';
  }
  return fromSettings;
}

function getCustomerAccountShopUrlForOauth() {
  if (typeof window === 'undefined') return '';
  const acc = window.customerAccount || (window.siteGlobals && window.siteGlobals.customerAccount);
  const raw = acc && acc.oauthOrigin != null ? String(acc.oauthOrigin).trim() : '';
  if (!raw) return '';
  return raw.replace(/\/+$/, '');
}

function maybeRedirectToShopCanonicalOriginForCustomerAccountOAuth() {
  if (typeof window === 'undefined') return false;
  const href = getCustomerAccountShopUrlForOauth();
  if (!href) return false;
  let targetOrigin = '';
  try {
    targetOrigin = new URL(href).origin;
  } catch (e) {
    void e;
    return false;
  }
  if (!targetOrigin || targetOrigin === window.location.origin) return false;
  let pend = null;
  try {
    pend = sessionStorage.getItem(CA.ssPendingCookieOptIn);
  } catch (e) {
    void e;
  }
  if (pend !== 'true' && pend !== 'false') return false;
  try {
    localStorage.setItem(CA.lsBridgeResume, '1');
    localStorage.setItem(CA.lsBridgePending, pend);
    sessionStorage.removeItem(CA.ssPendingCookieOptIn);
  } catch (e) {
    void e;
    return false;
  }
  window.location.replace(targetOrigin + window.location.pathname + window.location.search + window.location.hash);
  return true;
}

function consumeCustomerAccountOauthBridge() {
  if (typeof window === 'undefined') return;
  let flag = '';
  try {
    flag = localStorage.getItem(CA.lsBridgeResume) || '';
  } catch (e) {
    void e;
    return;
  }
  if (flag !== '1') return;
  let pend = null;
  try {
    pend = localStorage.getItem(CA.lsBridgePending);
    localStorage.removeItem(CA.lsBridgeResume);
    localStorage.removeItem(CA.lsBridgePending);
  } catch (e) {
    void e;
  }
  if (pend !== 'true' && pend !== 'false') return;
  try {
    sessionStorage.setItem(CA.ssPendingCookieOptIn, pend);
  } catch (e) {
    void e;
  }
  void startCustomerAccountOAuth();
}

async function normalizeCustomerAccountGraphqlUrl(href) {
  if (typeof window === 'undefined' || !href) return String(href || '');
  let u;
  try {
    u = new URL(String(href).trim());
  } catch (e) {
    void e;
    return String(href);
  }
  if (!/\/customer\/api\/[^/]+\/graphql/.test(u.pathname)) {
    return u.toString();
  }
  const oidc = await resolveOidcConfiguration();
  if (!oidc || typeof oidc.token_endpoint !== 'string') return u.toString();
  let idpOrigin;
  try {
    idpOrigin = new URL(oidc.token_endpoint).origin;
  } catch (e) {
    void e;
    return u.toString();
  }
  if (idpOrigin === u.origin || u.origin !== window.location.origin) return u.toString();
  return `${idpOrigin}${u.pathname}${u.search}`;
}

export async function resolveCustomerAccountGraphqlUrl() {
  if (typeof window !== 'undefined' && window.__CA_API_GRAPHQL_URL__) {
    return normalizeCustomerAccountGraphqlUrl(String(window.__CA_API_GRAPHQL_URL__).trim());
  }
  const configured = getCustomerAccountApiEndpoint();
  if (configured) {
    const v = parseCustomerApiVersionFromGraphqlUrl(configured);
    if (v) cachedCustomerApiVersion = v;
    cachedGqlUrl = configured;
    return configured;
  }
  if (cachedGqlUrl) return cachedGqlUrl;
  const j = await fetchShopWellKnownJson('/.well-known/customer-account-api');
  if (j && typeof j.graphql_api === 'string') {
    const v = parseCustomerApiVersionFromGraphqlUrl(j.graphql_api);
    if (v) cachedCustomerApiVersion = v;
    cachedGqlUrl = await normalizeCustomerAccountGraphqlUrl(j.graphql_api);
    return cachedGqlUrl;
  }
  const oidc = await resolveOidcConfiguration();
  if (oidc && typeof oidc.token_endpoint === 'string') {
    const ver = cachedCustomerApiVersion || CUSTOMER_ACCOUNT_API_VER_FALLBACK;
    cachedGqlUrl = `${window.location.origin}/customer/api/${ver}/graphql`;
    return cachedGqlUrl;
  }
  cachedGqlUrl = `${window.location.origin}/customer/api/${CUSTOMER_ACCOUNT_API_VER_FALLBACK}/graphql`;
  return cachedGqlUrl;
}

export function persistCustomerAccountTokens(tokenResponse) {
  if (typeof window === 'undefined' || !tokenResponse || !tokenResponse.access_token) return;
  const access = String(tokenResponse.access_token).trim();
  try {
    localStorage.setItem(CA.lsAccess, access);
    if (tokenResponse.refresh_token) {
      localStorage.setItem(CA.lsRefresh, String(tokenResponse.refresh_token).trim());
    }
    const sec = Number(tokenResponse.expires_in);
    if (Number.isFinite(sec) && sec > 0) {
      localStorage.setItem(CA.lsExpiry, String(Date.now() + sec * 1000));
    }
  } catch (e) {
    void e;
  }
  window.__CUSTOMER_ACCOUNT_ACCESS_TOKEN__ = access;
}

export function clearCustomerAccountSession() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CA.lsAccess);
    localStorage.removeItem(CA.lsRefresh);
    localStorage.removeItem(CA.lsExpiry);
  } catch (e) {
    void e;
  }
  try {
    delete window.__CUSTOMER_ACCOUNT_ACCESS_TOKEN__;
    delete window.__DEV_CUSTOMER_ACCOUNT_ACCESS_TOKEN__;
  } catch (e) {
    window.__CUSTOMER_ACCOUNT_ACCESS_TOKEN__ = '';
    window.__DEV_CUSTOMER_ACCOUNT_ACCESS_TOKEN__ = '';
  }
}

export async function startCustomerAccountOAuth() {
  if (typeof window === 'undefined') return;
  const clientId = getCustomerAccountClientId();
  if (!clientId) {
    console.warn('Customer Account OAuth: set customer_account_client_id in theme settings.');
    return;
  }
  if (maybeRedirectToShopCanonicalOriginForCustomerAccountOAuth()) return;
  const oidc = await resolveOidcConfiguration();
  if (!oidc) {
    console.warn('Customer Account OAuth: could not load /.well-known/openid-configuration.');
    return;
  }
  const pathnameRaw = getCookieOAuthCallbackPath();
  const pathname = pathnameRaw.startsWith('/') ? pathnameRaw : `/${pathnameRaw}`;
  const redirectUri = `${window.location.origin}${pathname}`;
  const verifier = randomPkceVerifier();
  const challenge = await sha256Base64Url(verifier);
  const state = randomPkceVerifier().slice(0, 43);
  try {
    sessionStorage.setItem(CA.ssPkceVerifier, verifier);
    sessionStorage.setItem(CA.ssPkceState, state);
    sessionStorage.setItem(CA.ssOauthReturn, window.location.href);
    sessionStorage.setItem(CA.ssOauthRedirectUri, redirectUri);
  } catch (e) {
    void e;
  }
  const u = new URL(oidc.authorization_endpoint);
  u.searchParams.set('client_id', clientId);
  u.searchParams.set('redirect_uri', redirectUri);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('scope', CUSTOMER_ACCOUNT_OAUTH_SCOPE);
  u.searchParams.set('state', state);
  u.searchParams.set('code_challenge', challenge);
  u.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(u.toString());
}

function getCookieOAuthPersistedAccessToken() {
  if (typeof window === 'undefined') return '';
  const fromWindow = window.__CUSTOMER_ACCOUNT_ACCESS_TOKEN__ || window.__DEV_CUSTOMER_ACCOUNT_ACCESS_TOKEN__;
  if (fromWindow) return String(fromWindow).trim();
  try {
    const ls = localStorage.getItem(CA.lsAccess);
    if (ls) return ls.trim();
  } catch (e) {
    void e;
  }
  return '';
}

async function resolveAccessTokenForCustomerAccountApi() {
  const fromCookieFlow = getCookieOAuthPersistedAccessToken();
  if (fromCookieFlow) return fromCookieFlow;
  if (typeof window === 'undefined') return '';
  if (!window.customerAccountEnabled || !window.isLoggedIn) return '';
  try {
    const cap = await getB2bCustomerAccountAccessToken();
    return cap && String(cap).trim() ? String(cap).trim() : '';
  } catch (e) {
    void e;
    return '';
  }
}

function getStoredRefreshToken() {
  if (typeof window === 'undefined') return '';
  try {
    const r = localStorage.getItem(CA.lsRefresh);
    return r ? String(r).trim() : '';
  } catch (e) {
    void e;
  }
  return '';
}

export async function refreshCustomerAccountAccessToken() {
  if (typeof window === 'undefined') return false;
  if (customerAccountRefreshPromise) return customerAccountRefreshPromise;
  const p = (async () => {
    const clientId = getCustomerAccountClientId();
    const refreshToken = getStoredRefreshToken();
    if (!clientId || !refreshToken) return false;
    const oidc = await resolveOidcConfiguration();
    if (!oidc || typeof oidc.token_endpoint !== 'string') return false;
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('client_id', clientId);
    body.set('refresh_token', refreshToken);
    try {
      const res = await fetch(oidc.token_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json && json.access_token) {
        persistCustomerAccountTokens(json);
        return true;
      }
      if (json && json.error === 'invalid_grant') {
        clearCustomerAccountSession();
      }
    } catch (e) {
      void e;
    }
    return false;
  })();
  customerAccountRefreshPromise = p;
  try {
    return await p;
  } finally {
    if (customerAccountRefreshPromise === p) customerAccountRefreshPromise = null;
  }
}

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch (e) {
    void e;
    return null;
  }
}

async function customerAccountGraphqlRequest(payload) {
  let token = await resolveAccessTokenForCustomerAccountApi();
  if (!token) {
    throw caError('Customer Account access token required. Sign in via Headless Customer Account OAuth.', CA_ERROR_MISSING_ACCESS_TOKEN);
  }
  const url = await resolveCustomerAccountGraphqlUrl();
  const post = authToken =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: authToken },
      body: JSON.stringify(payload)
    });
  let res = await post(token);
  if (res.status === 401) {
    await refreshCustomerAccountAccessToken();
    token = await resolveAccessTokenForCustomerAccountApi();
    if (token) res = await post(token);
  }
  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw caError(`Customer Account API request failed (${res.status}).`, 'CA_HTTP_ERROR');
  }
  if (body && body.errors && body.errors.length) {
    throw caError(joinApiMessages(body.errors, 400) || 'Customer Account GraphQL error.', 'CA_GRAPHQL_ERROR');
  }
  return body;
}

export async function setCookieOptInMetafield(optIn) {
  if (typeof window !== 'undefined') {
    if (!window.customerAccountEnabled || window.isLoggedIn !== true) {
      return null;
    }
  }
  const idBody = await customerAccountGraphqlRequest({ query: caCustomerIdForMetafield });
  const ownerId = idBody && idBody.data && idBody.data.customer && idBody.data.customer.id;
  if (!ownerId || typeof ownerId !== 'string') {
    throw caError('Could not resolve customer id for Customer Account API.', 'CA_CUSTOMER_ID_UNAVAILABLE');
  }
  const decidedAt = new Date().toISOString();
  const metafields = [
    {
      ownerId,
      namespace: 'custom',
      key: 'cookie_opt_in',
      type: 'boolean',
      value: optIn ? 'true' : 'false'
    },
    {
      ownerId,
      namespace: 'custom',
      key: 'cookie_opt_date',
      type: 'date_time',
      value: decidedAt
    }
  ];
  const body = await customerAccountGraphqlRequest({
    query: caSetCookieOptInMetafield,
    variables: { metafields }
  });
  const payload = body && body.data && body.data.metafieldsSet;
  const ue = payload && payload.userErrors ? payload.userErrors : null;
  if (ue && ue.length) {
    throw caError(joinApiMessages(ue, 400) || 'Metafield update was rejected.', 'CA_METAFIELD_USER_ERROR');
  }
  return payload && payload.metafields ? payload.metafields : null;
}

function dispatchPendingCookieOptInResult(success, errorDetail) {
  if (typeof document === 'undefined') return;
  try {
    document.dispatchEvent(
      new CustomEvent(EVENT_CUSTOMER_ACCOUNT_PENDING_COOKIE_OPT_IN, {
        detail: success ? { success: true } : { success: false, error: errorDetail }
      })
    );
  } catch (e) {
    void e;
  }
}

export async function tryFinishPendingCookieOptIn() {
  if (typeof window === 'undefined') return;
  if (!window.customerAccountEnabled || window.isLoggedIn !== true) return;
  let pending = null;
  try {
    pending = sessionStorage.getItem(CA.ssPendingCookieOptIn);
  } catch (e) {
    void e;
    return;
  }
  if (pending == null || !(await resolveAccessTokenForCustomerAccountApi())) return;
  try {
    await setCookieOptInMetafield(pending === 'true');
    try {
      sessionStorage.removeItem(CA.ssPendingCookieOptIn);
    } catch (e) {
      void e;
    }
    dispatchPendingCookieOptInResult(true);
  } catch (e) {
    const detail = e && typeof e === 'object' && 'message' in e ? String(e.message).slice(0, 240) : 'sync_failed';
    dispatchPendingCookieOptInResult(false, detail);
  }
}

function scheduleTryFinishPendingCookieOptIn() {
  const run = () => void tryFinishPendingCookieOptIn();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run, { once: true });
}

export function attachCustomerAccountCookieOAuthToWindow() {
  if (typeof window === 'undefined') return;
  consumeCustomerAccountOauthBridge();
  window.__customerAccountCookieOAuth = {
    setCookieOptInMetafield,
    persistCustomerAccountTokens,
    clearCustomerAccountSession,
    getCustomerAccountClientId,
    getCustomerAccountApiEndpoint,
    startCustomerAccountOAuth,
    tryFinishPendingCookieOptIn,
    refreshCustomerAccountAccessToken,
    CA_ERROR_MISSING_ACCESS_TOKEN,
    EVENT_CUSTOMER_ACCOUNT_PENDING_COOKIE_OPT_IN
  };
  if (typeof document === 'undefined') return;
  scheduleTryFinishPendingCookieOptIn();
}
